import {SelectionMode} from 'components/FluentUICompat';
import React, {useEffect, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {
	useGetVehicleProjectParagraphsNextLazyQuery,
	useGetVehicleProjectParagraphsSortOrderLazyQuery,
} from 'features/VehicleProjects/hooks/AssignedParagraphsInfinity.generated';
import {
	InfinityList,
	InfinityListColumn,
	InfinityListColumnMetaData,
	funcDecodeHex24ToDocId,
	InfinityListView,
} from 'components/InfinityList';
import {RegulatoryDocumentParagraph} from 'types';
import {useInfinityListStandardHooks} from 'components/InfinityList/InfinitListStandardHooks';
import {useGetRegDocParagraphIdsLazyQuery} from '../hooks/RegDocParagraphsInfinity.generated';
import {renderParagraph} from 'components/EntityList/ColumnRenderers';
import {useTranslation} from 'react-i18next';
import {useParagraphsContext} from '../components/DocumentDetails/ParagraphsContext';
import {useEntityContext} from 'components/EntityPage/EntityContext';

const initialSortField = 'position';
const initialSortOrder = 'ASC';
const intBatchSize = 50;
const HEX24_REGEX = /^[a-f0-9]{24}$/i;

const decodeToHex24Id = (value: string) => {
	if (HEX24_REGEX.test(value)) {
		return value;
	}

	const decodedValue = funcDecodeHex24ToDocId(value);
	return HEX24_REGEX.test(decodedValue) ? decodedValue : value;
};

export type RegDocDetailsPageParagraphsListProps = Readonly<{
	regulatoryDocumentId: string;
	tocFilteredParagraphIds?: string[];
}>;

export function RegDocDetailsPageParagraphsList({
	regulatoryDocumentId,
	tocFilteredParagraphIds = [],
}: RegDocDetailsPageParagraphsListProps) {
	const {selectedItems} = useEntityContext<RegulatoryDocumentParagraph>();
	const {setSelectedParagraphs} =
		useParagraphsContext<RegulatoryDocumentParagraph>();

	useEffect(() => {
		setSelectedParagraphs(selectedItems ?? []);
	}, [selectedItems, setSelectedParagraphs]);

	const {t} = useTranslation('features/regulatorydocuments', {
		keyPrefix: 'ParagraphsList',
	});

	const navigate = useNavigate();

	const [getRegDocParagraphIds] = useGetRegDocParagraphIdsLazyQuery();

	const [getParagraphsNext, {loading: loadingDocuments}] =
		useGetVehicleProjectParagraphsNextLazyQuery();

	const [getSortOrder] = useGetVehicleProjectParagraphsSortOrderLazyQuery();

	const views: InfinityListView[] = useMemo(() => [], []);
	const initialView = 'STD';
	const [totalCount, setTotalCount] = useState(0);

	const columns: InfinityListColumn[] = useMemo(
		() => [
			{
				key: 'position',
				name: t('Position'),
				fieldName: 'position',
				minWidth: 40,
				maxWidth: 60,
			},
			{
				key: 'paragraph',
				name: t('Paragraph'),
				fieldName: 'paragraph',
				minWidth: 500,
				isResizable: true,
				isMultiline: true,
				onRender: (item: RegulatoryDocumentParagraph) =>
					renderParagraph(item, t),
			},
			{
				key: 'keywords',
				name: t('Keywords'),
				fieldName: 'keywords',
				minWidth: 150,
				maxWidth: 200,
				isResizable: true,
				isMultiline: true,
				onRender: (item: RegulatoryDocumentParagraph) =>
					item.keywords?.map(k => k.name).join(', ') ?? '',
			},
			{
				key: 'categories',
				name: t('Categories'),
				fieldName: 'categories',
				minWidth: 150,
				maxWidth: 200,
				isResizable: true,
				isMultiline: true,
				onRender: (item: RegulatoryDocumentParagraph) =>
					item.categories?.map(c => c.name).join(', ') ?? '',
			},
		],
		[t],
	);

	const columnMetaData: InfinityListColumnMetaData[] = useMemo(() => [], []);

	const queryDocumentsDB = async (
		_query: unknown,
		_sortField: string,
		_sortOrder: string,
	) => {
		const {data: idsData} = await getRegDocParagraphIds({
			variables: {regulatoryDocumentId},
		});

		const tocFilteredIdsSet = new Set(tocFilteredParagraphIds.map(decodeToHex24Id));

		const allParaIds = (idsData?.regulatoryDocument?.paragraphs ?? [])
			.filter(
				p =>
					tocFilteredParagraphIds.length === 0 ||
					tocFilteredIdsSet.has(decodeToHex24Id(p.id)),
			)
			.map(p => ({
				regDocId: decodeToHex24Id(regulatoryDocumentId),
				paragraphId: decodeToHex24Id(p.id),
			}));

		// Use plain paragraph IDs as list IDs to keep pagination/sorting IDs
		// aligned with loaded document IDs.
		const arrIDsNew = allParaIds.map(p => p.paragraphId);
		setTotalCount(arrIDsNew.length);

		const firstBatchIds = allParaIds.slice(0, intBatchSize);
		const {data: batchData} = await getParagraphsNext({
			variables: {nextParaArgs: {preFilteredParagraphIds: firstBatchIds}},
		});

		const arrDocumentNext = (batchData?.paragraphsNext ??
			[]) as RegulatoryDocumentParagraph[];

		return {arrDocumentNext, arrIDsNew};
	};

	const getDocumentsNextDB = async (preFilteredIds: string[]) => {
		const preFilteredParagraphIds = preFilteredIds.map(paragraphId => ({
			regDocId: decodeToHex24Id(regulatoryDocumentId),
			paragraphId: decodeToHex24Id(paragraphId),
		}));

		const {data} = await getParagraphsNext({
			variables: {
				nextParaArgs: {
					preFilteredParagraphIds,
				},
			},
		});
		return (data?.paragraphsNext ?? []) as RegulatoryDocumentParagraph[];
	};

	const getSortOrderDB = async (
		preFilteredIds: string[],
		sortField: string,
	) => {
		const preFilteredParagraphIds = preFilteredIds.map(paragraphId => ({
			regDocId: decodeToHex24Id(regulatoryDocumentId),
			paragraphId: decodeToHex24Id(paragraphId),
		}));

		const {data} = await getSortOrder({
			variables: {
				sortParaArgs: {
					sortField,
					preFilteredParagraphIds,
				},
			},
		});
		return (data?.paragraphsSortOrder ?? []).map(p =>
			decodeToHex24Id(p.paragraphId),
		);
	};

	const combineViewAndUserFilterQuery = (
		queryUser: unknown,
		_views: InfinityListView[],
		_viewKey?: string,
	) => queryUser;

	const {
		arrDocument,
		keyPrefix,
		linksFilters,
		firstRenderSTD,
		applyFilterSTD,
		applySortSTD,
		loadItemsNextSTD,
	} = useInfinityListStandardHooks({
		views,
		intBatchSize,
		initialSortField,
		initialSortOrder,
		initialView,
		arrIds: [],
		generateLink: false,
		columns,
		columnMetaData,
		combineViewAndUserFilterQuery,
		queryDocumentsDB,
		getDocumentsNextDB,
		getSortOrderDB,
	});

	const handleViewingItem = (item: RegulatoryDocumentParagraph) => {
		navigate({pathname: `/requirements/${item.id}`});
	};

	const createHref = (_item: unknown) => `/requirements/`;

	return (
		<InfinityList
			pageName={''}
			items={arrDocument}
			sticky={false}
			columns={columns}
			columnMetaData={columnMetaData}
			columnFilterOptions={[]}
			selectionMode={SelectionMode.multiple}
			stillLoading={loadingDocuments}
			initalFilters={linksFilters}
			initialSortField={initialSortField}
			initialSortOrder={initialSortOrder}
			initialView={initialView}
			keyPrefix={keyPrefix}
			firstRender={firstRenderSTD}
			loadItemsNext={loadItemsNextSTD}
			applyFilter={applyFilterSTD}
			applySort={applySortSTD}
			handleInvokeItem={handleViewingItem}
			createHref={createHref}
			numberOfElements={totalCount}
			showHeader={false}
			views={views}
			advancedFilterShow={false}
		/>
	);
}
